import axiosLib, { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import ExifReader from "exifreader";
import bcrypt from "bcryptjs";
import { cleanupExif } from "./cleanup-exif";
import { UploadType } from "contexts/UploadContext";
import {
  getCollectionDetails,
  StoredCollectionDetails,
} from "./get-collection-details";
import { getExtensionFromMime } from "./mime-to-ext";
import { getVideoMetadata } from "./get-video-metadata";
import { Storage, StorageKey } from "./storage";
import { isZipFile } from "./upload-files";

const axios = axiosLib.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30000,
});

// NOTE: add token to request headers
axios.interceptors.request.use((config) => {
  const token = Storage.get(StorageKey.TOKEN);
  if (window && token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// NOTE: api error interceptor
axios.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e instanceof AxiosError && e.response) {
      throw new ApiError(e.response);
    }

    throw e;
  }
);

export class ApiError extends Error {
  status: number;
  code: string;
  detail: string;
  attr: string;

  constructor(response: AxiosResponse) {
    super(response.data.detail);
    this.status = response.status;
    this.code = response.data.code;
    this.detail = response.data.detail;
    this.attr = response.data.attr;
  }
}

export interface PiiDeletionMetadata {
  deletion_id: string;
  opt_out: ApiCheckPiiStatus;
  opt_out_timestamp: string;
  deleted_data_counts: Record<string, number>;
}

export interface StepMetadata {
  step_number: number;
  step_label: string;
  step_title: string;
  number_of_captures: number;
  step_details?: Array<string>;
}

export enum ApiCheckPiiStatus {
  REQUESTED = "Requested",
  COMPLETED = "Completed",
}

export enum ApiCheckPiiStatusCode {
  NOT_FOUND = "not_found",
  INVALID = "invalid",
}

export enum UploadPath {
  FACE_CAPTURE = "/face_capture",
  VIDEO_CAPTURE = "/video_capture",
  IRIS_CAPTURE = "/iris_capture",
  PCR_CAPTURE = "/pcr_capture",
}

export interface VideoMetadata {
  PixelXDimension: {
    value: number;
    description: string;
  };
  PixelYDimension: {
    value: number;
    description: string;
  };
  "Image Width": {
    value: number;
    description: string;
  };
  "Image Height": {
    value: number;
    description: string;
  };
}

function getApiUploadUrl(
  uploadType = UploadType.REGULAR,
  isImage: boolean
): UploadPath {
  switch (uploadType) {
    case UploadType.REGULAR:
      return isImage ? UploadPath.FACE_CAPTURE : UploadPath.VIDEO_CAPTURE;
    case UploadType.IRIS:
      return UploadPath.IRIS_CAPTURE;
    case UploadType.PCR:
      return UploadPath.PCR_CAPTURE;
  }
}

export const apiUploadFile = async (
  file: File,
  capture_id: string,
  uploadType = UploadType.REGULAR,
  task: string,
  stepMetadata?: StepMetadata,
  captureCollectionDetails?: StoredCollectionDetails | null,
  sessionMetadata?: Record<string, string>
) => {
  const isImage = file.type.startsWith("image/");
  const isZip = isZipFile(file);
  const ext = isZip ? ".zip" : getExtensionFromMime(file.type);

  let exif: ExifReader.Tags | VideoMetadata | undefined = undefined;

  // For PCR uploads, we expect zip files and don't need to extract EXIF data
  if (uploadType === UploadType.PCR) {
    if (!isZip) {
      const message = "Only ZIP files are accepted for PCR uploads.";
      toast.error(message);
      throw new Error(message);
    }
  } else if (isImage) {
    try {
      exif = await ExifReader.load(file);
    } catch (err) {
      console.log("Cannot read exif data", err);
    }
  } else if (!isZip) {
    exif = await getVideoMetadata(file);
    if (exif.PixelXDimension.value > exif.PixelYDimension.value) {
      const message =
        "Only portrait videos are accepted. Please select a different file.";
      toast.error(message);
      throw new Error(message);
    }
  }

  const collectionDetails =
    sessionMetadata === undefined
      ? await getCollectionDetails(task, captureCollectionDetails)
      : await getCollectionDetails(
          task,
          captureCollectionDetails,
          undefined,
          sessionMetadata
        );

  const now = new Date();

  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  const timestamp = `${day}${month}${year}_${hours}${minutes}`;

  const params: {
    file_extension: string;
    exif?: object;
    capture_id: string;
    collection_details?: Array<string>;
    timestamp: string;
    task_id: string;
    step_metadata?: StepMetadata;
  } = {
    file_extension: ext,
    collection_details: collectionDetails?.collection_details,
    exif: cleanupExif({
      ...exif,
      ...collectionDetails?.exif,
    }),
    capture_id,
    timestamp,
    task_id: task,
    step_metadata: stepMetadata,
  };

  const apiUrl = getApiUploadUrl(uploadType, isImage);

  const response = await axios.post(apiUrl, params);

  const { url, fields } = response.data;

  // Retry only the idempotent transfer to this existing presigned key. The
  // backend request above must not be repeated because it creates a record.
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const s3Response = await axiosLib.postForm(
        url,
        { ...fields, file },
        { timeout: 300000 }
      );
      if (s3Response.status !== 201 || !s3Response.data?.includes("<ETag>")) {
        throw new Error("S3 upload verification failed");
      }
      return;
    } catch (error) {
      const status = axiosLib.isAxiosError(error)
        ? error.response?.status
        : undefined;
      const retryable =
        (error instanceof Error &&
          error.message === "S3 upload verification failed") ||
        (axiosLib.isAxiosError(error) &&
          (status === undefined ||
            status === 408 ||
            status === 429 ||
            status >= 500));
      if (!retryable || attempt === 2) throw error;
      const delay = 250 * 2 ** attempt + Math.random() * 100;
      await new Promise((resolve) => window.setTimeout(resolve, delay));
    }
  }
};

export const apiLogin = async (
  email: string,
  password: string
): Promise<string | never> => {
  return (
    await axios.post("/login", {
      email,
      password,
      intent: "login", // Explicitly indicate this is a login attempt
    })
  ).data.token;
};

export const apiSignupLogin = async (
  email: string,
  passphrase: string
): Promise<string | never> => {
  // For signup, bcrypt hash the campaign passphrase like the Welcome screen does
  return (
    await axios.post("/login", {
      email,
      password: bcrypt.hashSync(passphrase, 10),
      intent: "signup", // Explicitly indicate this is a signup attempt
    })
  ).data.token;
};

export const apiSetupPasswordLogin = async (
  email: string
): Promise<string | never> => {
  return (
    await axios.post("/setup-password-login", {
      email,
    })
  ).data.token;
};

export const apiValidatePassphrase = async (
  passphrase: string
): Promise<string | never> => {
  return (
    await axios.post("/validate-passphrase", {
      passphrase: bcrypt.hashSync(passphrase, 10),
    })
  ).data.token;
};

export const apiSendVerificationCode = async (
  email: string,
  isOriginalEmail: boolean = false
): Promise<{ success: boolean; message: string } | never> => {
  return (
    await axios.post("/send-verification-code", {
      email,
      is_original_email: isOriginalEmail,
    })
  ).data;
};

export const apiVerifyCode = async (
  email: string,
  code: string
): Promise<{ success: boolean; verified: boolean } | never> => {
  return (
    await axios.post("/verify-code", {
      email,
      code,
    })
  ).data;
};

export const apiSetPassword = async (
  email: string,
  password: string,
  originalEmail?: string,
  refreshSession = false
): Promise<{ success: boolean; message: string; token?: string } | never> => {
  // Send plaintext password - backend handles bcrypt hashing
  return (
    await axios.post("/set-password", {
      email,
      password: password,
      original_email: originalEmail,
      ...(refreshSession ? { refresh_session: true } : {}),
    })
  ).data;
};

export const apiSignup = async (
  bio_data: Record<string, string | boolean>
): Promise<string | never> => {
  return (await axios.post("/signup", { bio_data })).data.token;
};

export const apiCheckPii = async (
  deletionId: string
): Promise<PiiDeletionMetadata> => {
  return (await axios.get(`/pii?deletion_id=${deletionId}`)).data
    .deletion_metadata;
};

export const apiDeletePii = async () => {
  return (await axios.delete("/pii")).data.deletion_id;
};

export const apiGetUserProfile = async (): Promise<Record<string, any>> => {
  const response = await axios.get("/user/profile");
  return response.data.additionalData || {};
};

export const apiCheckReconsent = async (): Promise<boolean> => {
  const response = await axios.get("/user/profile");
  return response.data.needs_reconsent || false;
};

export const apiReconsent = async (): Promise<{ success: boolean }> => {
  const response = await axios.post("/user/reconsent");
  return response.data;
};

export const apiUpdateUserProfile = async (
  additionalData: Record<string, any>
): Promise<boolean> => {
  const response = await axios.put("/user/profile", { additionalData });
  return response.data.success || false;
};

export const apiRegisterWard = async (
  bio_data: Record<string, string | boolean>,
  email: string,
  password: string
): Promise<{ success: boolean; ward_user_id: string }> => {
  const response = await axios.post("/guardian/register-ward", {
    bio_data,
    email,
    password,
  });
  return response.data;
};

export const apiGetGuardianWards = async (): Promise<
  Array<{
    user_id: string;
    firstname: string;
    lastname: string;
    date_of_birth: string;
    email: string;
  }>
> => {
  const response = await axios.get("/guardian/wards");
  return response.data.wards || [];
};

export const apiSwitchUser = async (wardUserId?: string): Promise<string> => {
  const response = await axios.post("/guardian/switch-user", {
    ward_user_id: wardUserId || null,
  });
  return response.data.token;
};

export const apiGetSessionProgress = async (
  sessionId: string,
  collectionId: string
): Promise<Record<string, number>> => {
  const makeRequest = async () => {
    const token = Storage.get(StorageKey.TOKEN);
    return axios.get(
      `/session-progress?session_id=${sessionId}&collection_id=${collectionId}`,
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
    );
  };

  try {
    const response = await makeRequest();
    return response.data.progress || {};
  } catch (err) {
    // On first-load race, token might not be attached yet. Retry once after micro-delay.
    if (err instanceof ApiError && err.status === 401) {
      await new Promise((r) => setTimeout(r, 100));
      const response = await makeRequest();
      return response.data.progress || {};
    }
    throw err;
  }
};
